import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Button, Divider, Paper, Stack } from '@mui/material';
import type { GetDynamicInvoiceDataResponse } from '@weft/api-definition';
import { formatPrice } from '@weft/services';
import { INVOICE_REVIEW_STATUSES, type Currency } from '@weft/types';
import { isNotEmptyString, isNotNullOrUndefined } from '@weft/utils';
import { useVyNavigate } from 'apps/app/src/hooks/use-vy-navigate';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { useCallback, useMemo } from 'react';
import type { QueryKey } from 'react-query';
import { useQueryClient } from 'react-query';
import { NavLink } from 'react-router-dom';
import { useUpdateInvoicesReviewStatuses } from '../../../../../hooks/requests/use-update-invoices-review-statuses';
import { useNotificationTimeout } from '../../../../../hooks/use-notification-timeout';
import { DynamicInvoiceApprovedLabel } from './dynamic-invoice-header-components/DynamicInvoiceApprovedLabel';
import { DynamicInvoiceDetail } from './dynamic-invoice-header-components/DynamicInvoiceDetails';
import { InvoiceHeaderInfo } from './dynamic-invoice-header-components/DynamicInvoiceHeaderInfo';

type DynamicInvoiceHeaderProps = {
  dynamicInvoiceDataResponse: GetDynamicInvoiceDataResponse;
  currency: Currency;
  queryKeyToInvalidate?: QueryKey;
  noRedirectOnApprove?: boolean;
};

export const DynamicInvoiceHeader: React.FC<DynamicInvoiceHeaderProps> = ({
  dynamicInvoiceDataResponse: { dynamicInvoiceData },
  currency,
  queryKeyToInvalidate,
  noRedirectOnApprove = false,
})=>{
  const { invoice, customer } = dynamicInvoiceData;
  const { id: invoiceId, revenueBreakdown, reviewStatus, contractId } = invoice;
  const isInvoiceApproved = reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const availableBalance = revenueBreakdown.creditsUsed;
  const queryClient = useQueryClient();

  const navigate = useVyNavigate();

  const isInvoiceInReview = useMemo(() => reviewStatus === INVOICE_REVIEW_STATUSES.InReview, [reviewStatus]);
  
  const updateInvoicesReviewStatusesMutation = useUpdateInvoicesReviewStatuses();
  
  useNotificationTimeout(updateInvoicesReviewStatusesMutation.isSuccess, updateInvoicesReviewStatusesMutation.reset);

  const handleApprove = useCallback(async () => {
    await runMutation(
      updateInvoicesReviewStatusesMutation,
      {
        invoicesByNewStatus: {
          [INVOICE_REVIEW_STATUSES.Approved]: [invoiceId],
        },      
      },
      {
        pending: 'Approving invoice...',
        success: 'Invoice approved',
        error: 'Failed to approve invoice',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: ({ queryKey }) => ['billing-cycle-page', 'billing-cycle-invoices', invoiceId].some((key) => queryKey.includes(key)),
          });

          if (!noRedirectOnApprove) {
            setTimeout(() => {
              navigate(`/invoices/overview/light/${invoiceId}`);
            }, 1500);
          }
        },
      });
  }, [invoiceId, navigate, noRedirectOnApprove, queryClient, updateInvoicesReviewStatusesMutation]);

  return (
    <Paper
      elevation={1}
      sx={{
        display: 'flex',
        maxHeight: '200px',
        flexDirection: 'column',
        gap: 2,
        padding: 3,
        border: 1,
        borderRadius: 2,
        borderColor: 'divider',
      }}
    >
      <InvoiceHeaderInfo
        customer={customer}
        invoice={invoice}
        currency={currency}
        queryKeyToInvalidate={queryKeyToInvalidate}
      />
      
      <Divider />

      <Stack
        direction='row'
        sx={{ alignItems: 'center', gap: 2 }}
      >
        {isNotNullOrUndefined(availableBalance) && (availableBalance > 0) && (
          <DynamicInvoiceDetail
            label="Available Balance"
            value={formatPrice(availableBalance, currency, 0, 0)}
          />
        )}

        {isNotEmptyString(contractId) && (
          <NavLink to={`/plans/contracts/${contractId}`}>
            <DynamicInvoiceDetail
              label="Contract Name"
              value={invoice.contractName ?? contractId}
            />
          </NavLink>
        )}

        <DynamicInvoiceDetail
          label="Proforma Invoice"
          value={getProformaInvoiceId(invoiceId)}
          showDivider={false}
        />
 
        { isInvoiceApproved ? (
          <DynamicInvoiceApprovedLabel approvedAt={invoice.approvedAt} />
        )
          : (
            <Button
              disabled={!isInvoiceInReview || updateInvoicesReviewStatusesMutation.isLoading}
              onClick={handleApprove}
              variant="contained"
              color="primary"
              size="large"
              startIcon={<CheckCircleOutlineIcon />}
              sx={{ marginLeft: 'auto' }}
            >
              Approve
            </Button>)}
      </Stack>
    </Paper>
  );
};

function getProformaInvoiceId(invoiceId: string) {
  return invoiceId.substring(invoiceId.length - 5);
}